import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { FileText } from 'lucide-react';

/**
 * Renders an Issue Memo number as a clickable link to `/issue-memos/:id`.
 *
 * Accepts either:
 *  - `id` (the goldsmith_issues._id / goldsmith_issue_id) — preferred when known.
 *  - `memoNo` (the issue_memo_no string like TES/JUNE/26-27/001) — will resolve to id
 *    via `/api/goldsmith-issues/by-memo/<memo_no>` if `id` is not provided.
 *
 * If neither is present, renders a faded fallback chip with the provided fallback text.
 */
export default function MemoLink({
  id,
  memoNo,
  fallback = '—',
  className = '',
  testId,
  iconSize = 10,
}) {
  // Effective id we use to build the URL. Comes from prop directly, OR resolved via memoNo.
  const [resolvedId, setResolvedId] = useState(id || null);
  const lastQueriedRef = useRef(null);

  useEffect(() => {
    if (id) {
      // Direct id from prop — just sync via a microtask to satisfy lint
      Promise.resolve().then(() => setResolvedId(id));
      return;
    }
    if (!memoNo) {
      Promise.resolve().then(() => setResolvedId(null));
      return;
    }
    if (lastQueriedRef.current === memoNo) return;
    lastQueriedRef.current = memoNo;
    let cancelled = false;
    api
      .get(`/goldsmith-issues/by-memo/${encodeURIComponent(memoNo)}`)
      .then(({ data }) => { if (!cancelled) setResolvedId(data?.id || null); })
      .catch(() => { if (!cancelled) setResolvedId(null); });
    return () => { cancelled = true; };
  }, [id, memoNo]);

  const label = memoNo || '—';
  const baseCls = `inline-flex items-center gap-1 font-mono text-[11px] px-1.5 py-0.5 rounded ${className}`;

  if (resolvedId) {
    return (
      <Link
        to={`/issue-memos/${resolvedId}`}
        className={`${baseCls} bg-[var(--diamond-bg)] text-[var(--diamond-icon)] hover:underline`}
        data-testid={testId || 'memo-link'}
        title={`Open memo ${label}`}
      >
        <FileText size={iconSize} />
        {label}
      </Link>
    );
  }
  return (
    <span
      className={`${baseCls} bg-slate-100 text-slate-500`}
      data-testid={testId || 'memo-link'}
      title="Memo unavailable"
    >
      <FileText size={iconSize} />
      {label || fallback}
    </span>
  );
}
